import { useEffect, useMemo, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { bookBoxItems } from './data'
import { getSortedItems } from './utils'

export function useBookBoxShelf() {
  const navigate = useNavigate()
  const { category } = useParams()

  const [searchTerm, setSearchTerm] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [selectedGenre, setSelectedGenre] = useState('')
  const [sortOption, setSortOption] = useState('name')
  const [darkMode, setDarkMode] = useState(false)
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null)
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })

  useEffect(() => {
    setDarkMode(localStorage.getItem('darkMode') === 'true')
    const handleMouseMove = (e: MouseEvent) =>
      setMousePosition({ x: e.clientX, y: e.clientY })
    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])

  const genres = useMemo(
    () => [...new Set(bookBoxItems.map((b) => b.genre))],
    [],
  )

  const filteredItems = useMemo(() => {
    return bookBoxItems.filter((item) => {
      const matchesSearch =
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.theme.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesGenre = selectedGenre === '' || item.genre === selectedGenre
      if (category === 'active')
        return (
          matchesSearch &&
          matchesGenre &&
          item.status === 'active' &&
          !item.isSpecialEdition
        )
      if (category === 'past')
        return matchesSearch && matchesGenre && item.status === 'retired'
      if (category === 'special')
        return matchesSearch && matchesGenre && item.isSpecialEdition
      return matchesSearch && matchesGenre
    })
  }, [searchTerm, selectedGenre, category])

  const sortedItems = useMemo(
    () => getSortedItems(filteredItems, sortOption),
    [filteredItems, sortOption],
  )

  const navigateToCategory = (cat: string) => navigate(`/bookbox/shelf/${cat}`)
  const navigateToEdit = (id: string) => navigate(`/book-box/${id}/edit`)

  return {
    category,
    searchTerm,
    setSearchTerm,
    showFilters,
    setShowFilters,
    selectedGenre,
    setSelectedGenre,
    sortOption,
    setSortOption,
    darkMode,
    hoveredItemId,
    setHoveredItemId,
    mousePosition,
    genres,
    sortedItems,
    navigateToCategory,
    navigateToEdit,
    navigate,
  }
}
